"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAtom } from "jotai";
import { Copy, Download } from "lucide-react";
import { NextIntlClientProvider } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { parse, resync, stringify } from "subtitletools";
import * as z from "zod";
import { addMessage } from "@/components/Messages";
import { Button } from "@/components/ui/button";
import * as RadixForm from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { SubtitlesForm } from "@/lib/subtitletools/forms";
import { ResultDialog } from "@/lib/subtitletools/ResultDialog";
import { addRecentSubtitle, recentSubtitlesAtom } from "@/lib/subtitletools/recentSubtitles";
import enMessages from "@/messages/en.json";

export const ShiftSubtitlesContent = () => {
  const [shiftedSrt, setShiftedSrt] = useState("");
  const [loadingState, setLoadingState] = useState("INITIAL");
  const [error, setError] = useState<string | null>(null);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [recentSubtitles, setRecentSubtitles] = useAtom(recentSubtitlesAtom);

  const formSchema = z.object({
    subtitleText: z.string().min(1, "Please provide subtitle content"),
    shiftAmount: z.number().int(),
    preventNegativeStart: z.boolean().default(true),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      subtitleText: "",
      shiftAmount: 0,
      preventNegativeStart: true,
    },
  });

  const handleSubmit = (formData: z.infer<typeof formSchema>) => {
    setLoadingState("LOADING");
    setError(null);

    try {
      // Parse the subtitle content
      const captions = parse(formData.subtitleText);

      // Resync with the specified offset
      const shiftedCaptions = resync(captions, {
        offset: formData.shiftAmount,
        preventNegativeStart: formData.preventNegativeStart,
      });

      // Convert back to SRT format
      const result = stringify(shiftedCaptions, { format: "srt" });

      setShiftedSrt(result);
      setLoadingState("SUCCESS");
      setShowResultDialog(true);

      // Save shifted subtitle to recent subtitles
      try {
        const duration =
          shiftedCaptions.length > 0
            ? Math.max(...shiftedCaptions.map((c) => c.end)) -
              Math.min(...shiftedCaptions.map((c) => c.start))
            : 0;

        setRecentSubtitles(
          addRecentSubtitle(
            result,
            `Shifted subtitles (${shiftedCaptions.length} captions, ${formData.shiftAmount}ms offset)`,
            shiftedCaptions.length,
            duration
          )
        );
      } catch (error) {
        console.warn("Failed to save shifted subtitle:", error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to shift subtitles");
      setLoadingState("ERROR");
    }
  };

  const copyToClipboard = () => {
    if (navigator.clipboard && shiftedSrt) {
      navigator.clipboard.writeText(shiftedSrt);
      addMessage({
        text: "Copied to clipboard",
        type: "SUCCESS",
      });
    } else {
      addMessage({
        text: "Could not copy to clipboard",
        type: "ERROR",
      });
    }
  };

  const downloadFile = () => {
    const blob = new Blob([shiftedSrt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "shifted_subtitles.srt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl mb-8">
          Shift Subtitle Timestamps
        </h1>
        <div>
          <p>
            Upload or paste your subtitle file (.srt) and shift all timestamps by a specified
            amount.
          </p>
          <p>
            This is useful for synchronizing subtitles with video that has been trimmed or has
            different timing.
          </p>
        </div>
      </div>
      <NextIntlClientProvider messages={enMessages} locale={"en"} timeZone="Europe/Oslo">
        <div className="mb-6">
          <SubtitlesForm setInputSrt={(newValue) => form.setValue("subtitleText", newValue)} />
        </div>

        <RadixForm.Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <RadixForm.FormField
                control={form.control}
                name="shiftAmount"
                render={({ field }) => (
                  <RadixForm.FormItem>
                    <RadixForm.FormLabel>Shift Amount (milliseconds)</RadixForm.FormLabel>
                    <RadixForm.FormControl>
                      <Input
                        {...field}
                        type="text"
                        inputMode="numeric"
                        placeholder="0"
                        pattern="^-?\d*$"
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow empty string or just "-" for typing
                          if (value === "" || value === "-") {
                            field.onChange(value);
                          } else if (/^-?\d+$/.test(value)) {
                            // Only allow whole numbers (including negative)
                            field.onChange(parseInt(value, 10));
                          }
                        }}
                        value={
                          typeof field.value === "number"
                            ? field.value.toString()
                            : (field.value ?? "")
                        }
                      />
                    </RadixForm.FormControl>
                    <RadixForm.FormDescription>
                      1000 ms = 1 second. Positive values make the subtitles appear later, negative
                      values make them appear earlier.
                    </RadixForm.FormDescription>
                    <RadixForm.FormMessage />
                  </RadixForm.FormItem>
                )}
              />
            </div>

            <div className="flex space-x-2">
              <Button
                type="submit"
                loading={loadingState === "LOADING"}
                disabled={loadingState === "LOADING"}
              >
                Shift Subtitles
              </Button>
            </div>
          </form>
        </RadixForm.Form>
        <div className="mt-8">
          {loadingState === "LOADING" && (
            <Skeleton className="w-1/2 h-12 flex items-center justify-center text-foreground/40 rounded-xl" />
          )}

          {error && (
            <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
              <p className="text-red-800 font-medium">Error:</p>
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {shiftedSrt && loadingState === "SUCCESS" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Shifted Subtitles</h3>
                <div className="flex space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={copyToClipboard}
                    className="flex items-center space-x-2"
                  >
                    <Copy className="h-4 w-4" />
                    <span>Copy</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={downloadFile}
                    className="flex items-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download</span>
                  </Button>
                </div>
              </div>
              <Textarea value={shiftedSrt} rows={12} readOnly className="font-mono text-sm" />
            </div>
          )}
        </div>

        {/* Result Dialog */}
        <ResultDialog
          open={showResultDialog}
          onOpenChange={setShowResultDialog}
          title="Shift Complete"
          description="Successfully shifted subtitle timestamps"
          content={shiftedSrt}
          fileName="shifted_subtitles.srt"
        />
      </NextIntlClientProvider>
    </div>
  );
};
